import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import {
    blurAutocompleter,
    selectResultByIndex,
    setAutocompleteSelectedIndex,
} from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.actions';
import { AUTOCOMPLETER_STATE_SLICE_KEY } from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.reducer';

import { EXTRA_CATEGORY_ORDER } from 'SearchApp/ts/constants';
import {
    AutocompleteExtraLink,
    AutocompleteExtraLinkType,
    ExtraCategoriesAutocompleteResults,
    ExtraLinksSectionToogle,
} from 'SearchApp/ts/types/categories-autocomplete-results.type';
import { LooseObject } from 'Angular/interfaces/loose-object.interface';
import { AutocompleteStore } from 'SearchApp/ts/features/base-autocomplete/state/interfaces/autocomplete-store.interface';
import { AutocompleteResult } from 'SearchApp/ts/interfaces/autocomplete-result.interface';

import { AccountsService } from 'AccountsApp/ts/services/accounts.service';

/**
 *
 * Extra Links Component
 *
 * This component is responsible for:
 *   1. Displaying extra links in autocompleter
 *   2. Handling when a result is clicked and hovered
 *   3. Displaying no results layout
 *
 * Input Properties:
 *    autocompleterId: The UUID that was created by the parent component to manage NgRx state.
 */

@Component({ standalone: false, selector: 'ycn-extra-links', templateUrl: './extra-links.template.html' })
export class ExtraLinksComponent implements OnInit, OnDestroy {
    @Input() autocompleterId!: string;

    // Handle blurring the search input (closing default links box) after it has lost focus in areas of the component
    // that do not navigate
    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(event.target) && this.clickInsideComponent) {
            this.store.dispatch(blurAutocompleter({ autocompleterId: this.autocompleterId }));
            this.clickInsideComponent = false;
        }
    }

    extraLinksCategories: ExtraCategoriesAutocompleteResults = {};

    extraCategoryResults: AutocompleteExtraLinkType[] = [];

    extraLinksHeaders: { [k in AutocompleteExtraLinkType]?: ExtraLinksSectionToogle } = {
        asset: { label: 'Recent Activity', isOpen: true },
        security: { label: 'Recently Visited', isOpen: true },
    };

    flatResults: (AutocompleteResult | AutocompleteExtraLink)[] = [];

    inputText: string = '';

    isFetching: boolean = false;

    selectedIndex: number = -1;

    showResults: boolean = false;

    showNoResultsLayout: boolean = false;

    prevShowNoResultsLayout: boolean = false;

    clickInsideComponent: boolean = false;

    private storeSubscription: Subscription;

    constructor(
        private elementRef: ElementRef,
        private accountsService: AccountsService,
        readonly store: Store<{ autocompleter: AutocompleteStore }>
    ) {
        this.storeSubscription = this.store.select(AUTOCOMPLETER_STATE_SLICE_KEY).subscribe(this.mapStateToComponent);
    }

    ngOnInit(): void {
        this.accountsService.getUserPreferences().subscribe((preference) => {
            const preferenceHeaderSearchSections = preference.header_search_sections_toggle;
            if (!preferenceHeaderSearchSections || Object.keys(preferenceHeaderSearchSections).length === 0) {
                return;
            }

            this.extraLinksHeaders = (Object.keys(this.extraLinksHeaders) as AutocompleteExtraLinkType[]).reduce(
                (acc, section) => {
                    const isOpen =
                        preferenceHeaderSearchSections[section] !== undefined
                            ? preferenceHeaderSearchSections[section]
                            : this.extraLinksHeaders[section]?.isOpen;
                    return {
                        ...acc,
                        [section]: {
                            label: this.extraLinksHeaders[section]?.label,
                            isOpen,
                        } as ExtraLinksSectionToogle,
                    };
                },
                {} as LooseObject
            );
        });
    }

    ngOnDestroy(): void {
        this.storeSubscription.unsubscribe();
    }

    mapStateToComponent = (state: AutocompleteStore): void => {
        const autocompleterState = state[this.autocompleterId];
        if (!autocompleterState) {
            return;
        }
        const { selectedIndex, results, extraLinks, inputText, showResults, isFetching } = autocompleterState;
        this.selectedIndex = selectedIndex;
        this.flatResults = [...results, ...extraLinks];
        this.inputText = inputText;
        this.showResults = showResults;
        this.isFetching = isFetching;

        // keep no results layout shown if its fetching but it was already shown
        this.showNoResultsLayout =
            this.inputText.trim() !== '' &&
            !this.showResults &&
            (!this.isFetching || (this.isFetching && this.showNoResultsLayout));
        this.prevShowNoResultsLayout = this.showNoResultsLayout;

        // Convert extra links to dict of categories
        this.extraLinksCategories = extraLinks.reduce(
            (extraLinksDict: ExtraCategoriesAutocompleteResults, extraLink) => {
                const { type } = extraLink;
                return {
                    ...extraLinksDict,
                    [type]: !extraLinksDict[type]
                        ? [extraLink]
                        : [...(extraLinksDict[type] as AutocompleteExtraLink[]), extraLink],
                };
            },
            {}
        );
        this.extraCategoryResults = EXTRA_CATEGORY_ORDER.filter((category) => this.extraLinksCategories[category]);
    };

    clickResult(): void {
        this.store.dispatch(selectResultByIndex({ autocompleterId: this.autocompleterId }));
    }

    public getAILinkPrefix(): string {
        const hasInput = this.inputText.trim() !== '';
        return hasInput ? `Send "${this.inputText}" as a prompt to Y, the new` : 'Try Y, your new';
    }

    setSelectedIndexOnMouseEnter(result: AutocompleteResult | AutocompleteExtraLink): void {
        // changes selected index on mouseover so highlighted item can be submitted via enter or submit.
        const selectedIndex = this.flatResults.indexOf(result);
        this.store.dispatch(setAutocompleteSelectedIndex({ autocompleterId: this.autocompleterId, selectedIndex }));
    }

    toggleSection(category: AutocompleteExtraLinkType): void {
        if (this.extraLinksHeaders[category] !== undefined) {
            const { isOpen } = this.extraLinksHeaders[category] as ExtraLinksSectionToogle;
            this.extraLinksHeaders[category] = {
                ...(this.extraLinksHeaders[category] as ExtraLinksSectionToogle),
                isOpen: !isOpen,
            };
            const headerSectionsToggle = (Object.keys(this.extraLinksHeaders) as AutocompleteExtraLinkType[]).reduce(
                (acc, section) => ({
                    ...acc,
                    [section]: this.extraLinksHeaders[section]?.isOpen,
                }),
                {} as LooseObject
            );
            const params = { header_search_sections_toggle: JSON.stringify(headerSectionsToggle) };
            this.accountsService.putUserPreferences(params).subscribe();
        }
    }
}
